/**
 * UserLocationLayer
 *
 * Top-level map overlay for live location. ALWAYS renders the local user's own
 * dot (driven by `map.liveLocation`). When `localOnly` is false (the default,
 * authenticated map) it ALSO renders one marker per remote workspace member
 * broadcasting a position via the internal `RemoteMemberMarkers` part.
 *
 * On the public share map (`localOnly`), `RemoteMemberMarkers` is NOT mounted,
 * so none of the presence / workspace-member hooks it owns ever run — the
 * public map shows only the viewer's own dot with no broadcast or presence.
 * This layer itself subscribes only to `map.liveLocation`.
 *
 * @module components/map/UserLocationLayer
 */
import { memo } from 'react';

import { useGeoMarkupStore } from '@/store';

import { RemoteMemberMarkers } from './parts/RemoteMemberMarkers';
import { UserLocationMarker } from './parts/UserLocationMarker';

export interface UserLocationLayerProps {
  /**
   * When true, renders ONLY the local "you" dot and skips all remote-member
   * presence rendering (public share map). Defaults to false (authenticated
   * map: local dot + remote members).
   */
  localOnly?: boolean;
}

export const UserLocationLayer = memo(function UserLocationLayer({
  localOnly = false,
}: UserLocationLayerProps) {
  const liveLocation = useGeoMarkupStore((state) => state.map.liveLocation);

  return (
    <>
      {liveLocation && <UserLocationMarker location={liveLocation} />}
      {!localOnly && <RemoteMemberMarkers />}
    </>
  );
});
